import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex" data-testid="page-forgot-password">
      <div className="hidden lg:block lg:w-1/2 bg-sage relative">
        <div className="absolute inset-0 flex items-center justify-center p-16">
          <div className="text-cream max-w-md">
            <div className="font-serif-display text-5xl leading-[1.05]">
              No problem, lovely.
            </div>
            <p className="mt-6 text-cream/70 leading-relaxed">
              Pop your email below and I'll send you a link to choose a new password. The link's valid for one hour.
            </p>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="font-serif-display text-2xl text-ink" data-testid="forgot-logo">
            Pip<span className="text-gold italic"> Bennett</span>
          </Link>

          {done ? (
            <div className="mt-12" data-testid="forgot-success">
              <div className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-sage text-cream mb-6">
                <Check size={20} strokeWidth={1.5} />
              </div>
              <h1 className="font-serif-display text-4xl text-ink leading-tight">Check your inbox.</h1>
              <p className="mt-5 text-ink-soft leading-relaxed">
                If an account exists for <span className="text-ink">{email}</span>, a reset link is on its way. It's valid for the next hour. If it doesn't arrive in 5 minutes, check your spam folder or try again.
              </p>
              <Link
                to="/login"
                className="mt-10 inline-flex items-center gap-2 text-sage hover:text-sage-dark font-medium"
                data-testid="forgot-back-to-login"
              >
                Back to sign in
                <ArrowRight size={16} strokeWidth={1.8} />
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-serif-display text-4xl text-ink mt-10 leading-tight">Forgot your password?</h1>
              <p className="mt-3 text-ink-soft">Enter the email on your account and I'll send you a reset link.</p>

              <form onSubmit={submit} className="mt-10 space-y-5" data-testid="forgot-form">
                <div>
                  <label className="eyebrow block mb-2 text-ink-soft">Email</label>
                  <input
                    required
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-white border border-hairline rounded-full px-6 py-4 text-ink focus:outline-none focus:border-sage transition"
                    data-testid="forgot-email"
                  />
                </div>
                {error && (
                  <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-testid="forgot-error">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-sage hover:bg-sage-dark text-cream rounded-full px-8 py-4 font-medium tracking-wide transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="forgot-submit"
                >
                  {loading ? "Sending…" : "Send reset link"}
                  {!loading && <ArrowRight size={16} strokeWidth={1.8} />}
                </button>
              </form>

              <p className="mt-8 text-sm text-ink-soft">
                Remembered it?{" "}
                <Link to="/login" className="text-sage hover:text-sage-dark font-medium" data-testid="forgot-to-login">
                  Back to sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
